import { OAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { auth } from "hooks/useFirebase";
import { request } from "hooks/useOkoApi/useOkoApi";
import { constants } from "./general";
import { logAuth, logError, LogCategory, LogLevel } from "./sentryLogger";
import { PENDING_LOGIN_METHOD_KEY } from "./analytics";

const MICROSOFT_TENANT_MODE =
  process.env.REACT_APP_MICROSOFT_TENANT_MODE || "common";

export const MICROSOFT_OAUTH_STATE_KEY = "microsoft_oauth_state";
export const MICROSOFT_OAUTH_ORIGINAL_PATH = "microsoft_oauth_original_path";

// Marks this tab's sign-in as in progress so identify can count it as a Login.
// Storage can be unavailable (some private modes); tracking must never be what
// breaks a sign-in, so both helpers swallow that.
const markPendingSignIn = () => {
  try {
    sessionStorage.setItem(PENDING_LOGIN_METHOD_KEY, constants.MICROSOFT);
  } catch (_) {
    // No marker means no Login event; the sign-in itself is unaffected.
  }
};

// A failed sign-in must not leave its marker behind, or the next reload in
// this tab would be counted as a Login.
const clearPendingSignIn = () => {
  try {
    sessionStorage.removeItem(PENDING_LOGIN_METHOD_KEY);
  } catch (_) {
    // Nothing could have been written either.
  }
};

const buildMicrosoftProvider = () => {
  const provider = new OAuthProvider("microsoft.com");
  provider.setCustomParameters({
    tenant: MICROSOFT_TENANT_MODE,
    prompt: "select_account",
  });
  provider.addScope("email");
  provider.addScope("profile");
  return provider;
};

export const initiateMicrosoftSignIn = async (
  redirectUri,
  additionalState = {},
) => {
  const currentPath = window.location.pathname + window.location.search;
  const destination = redirectUri || currentPath;
  const isEducator = !!additionalState.isEducator;
  const isSignUp = !!additionalState.isSignUp;
  const hasGroupCode = !!additionalState.groupCode;

  try {
    logAuth("OAuth initiation started", {
      authMethod: constants.MICROSOFT,
      originPath: currentPath,
      destination,
      isEducator,
      isSignUp,
      hasGroupCode,
    });

    if (!isEducator) {
      localStorage.setItem(MICROSOFT_OAUTH_ORIGINAL_PATH, currentPath);
      const stateBlob = JSON.stringify({
        nonce: Math.random().toString(36).substring(2),
        destination,
        provider: constants.MICROSOFT,
        ...additionalState,
      });
      localStorage.setItem(MICROSOFT_OAUTH_STATE_KEY, stateBlob);
    }

    // Before the popup, so the marker is in place when the sign-in lands.
    if (isEducator) markPendingSignIn();

    const result = await signInWithPopup(auth, buildMicrosoftProvider());

    if (isEducator) {
      const idToken = await result.user.getIdToken();
      const endpoint = isSignUp
        ? "auth/microsoft/educator/signup/minimal"
        : "auth/microsoft/educator";

      try {
        await request(endpoint, {
          method: "POST",
          payload: { id_token: idToken },
          skipAuth: true,
        });
      } catch (e) {
        clearPendingSignIn();
        await signOut(auth);
        const errorStatus = Number(e?.status) || Number(e?.statusCode);
        logAuth("OAuth callback failed", {
          level: errorStatus === 403 ? LogLevel.WARNING : LogLevel.ERROR,
          authMethod: constants.MICROSOFT,
          originPath: currentPath,
          errorStatus: errorStatus || undefined,
          errorDetail: e?.detail,
          errorMessage: e?.message,
          isEducatorAttempt: true,
        });
        return { success: false, error: e, status: errorStatus };
      }

      const target = isSignUp ? "/educator/settings" : "/educator";
      logAuth("OAuth callback success", {
        authMethod: constants.MICROSOFT,
        userRole: "teacher",
        originPath: currentPath,
        redirectDestination: target,
        isNewEducator: isSignUp,
      });
      window.location.href = target;
      return { success: true };
    }

    logAuth("OAuth callback success", {
      authMethod: constants.MICROSOFT,
      originPath: currentPath,
      redirectDestination: destination,
    });
    if (destination && destination !== currentPath) {
      window.location.href = destination;
    }
    return { success: true };
  } catch (e) {
    clearPendingSignIn();
    if (
      e?.code === "auth/popup-closed-by-user" ||
      e?.code === "auth/cancelled-popup-request"
    ) {
      localStorage.removeItem(MICROSOFT_OAUTH_STATE_KEY);
      localStorage.removeItem(MICROSOFT_OAUTH_ORIGINAL_PATH);
      return { success: false, error: null };
    }
    localStorage.removeItem(MICROSOFT_OAUTH_STATE_KEY);
    localStorage.removeItem(MICROSOFT_OAUTH_ORIGINAL_PATH);
    logError("Microsoft OAuth initiation failed", e, {
      category: LogCategory.AUTH,
      authMethod: constants.MICROSOFT,
      originPath: currentPath,
      destination,
    });
    return { success: false, error: e };
  }
};
